import { calculateAge } from "../../../patient/utils/patientUtils";
import { NewBillPreviewData } from "./newBillPlaceholders";
import { mergeClinicPreviewData, NEW_BILL_SAMPLE_DATA } from "./newBillSampleData";
import { ReportTable } from "./newBillTypes";

function money(value: unknown) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "Rs. 0";
  return `Rs. ${n}`;
}

function formatInvoiceDate(value?: string | Date | null) {
  if (!value) return "";
  const dates = new Date(value);
  if (Number.isNaN(dates.getTime())) return "";
  const options: Intl.DateTimeFormatOptions = {
    timeZone: "Asia/Colombo",
    year: "numeric",
    month: "numeric",
    day: "numeric",
    hour: "numeric",
    minute: "numeric",
    hour12: true,
  };
  return dates
    .toLocaleString("en-GB", options)
    .replace(/\b(a\.?m\.?|p\.?m\.?)\b/gi, (m) => (m.startsWith("p") ? "PM" : "AM"))
    .replace(", ", " | ");
}

const isLongTermTreatment = (item: any) => item?.status === "long term";

const formatPrintSubTreatmentLabel = (items: any) => {
  const parts = [items?.treatment || "N/A"];
  if (items?.type) parts.push(items.type);
  if (items?.subtype) parts.push(items.subtype);
  if (items?.stage) parts.push(items.stage);
  return parts.join(" ");
};

const shouldShowPrintSubTreatment = (items: any, invoiceNo?: string) =>
  !!items &&
  !isLongTermTreatment(items) &&
  items.subInvoice === invoiceNo &&
  (items.selected_option === "treatment_paid" ||
    items.selected_option === "treatment_stage" ||
    items.selected_option === "treatment_non_paid");

function doctorLabel(doctor?: {
  firstName?: string;
  lastName?: string;
  drName?: string;
  title?: string;
}) {
  if (doctor?.drName) return String(doctor.drName).trim();
  const title = String(doctor?.title ?? "").trim();
  const first = String(doctor?.firstName ?? "").trim();
  const last = String(doctor?.lastName ?? "").trim();
  const name = `${first} ${last}`.trim();
  if (!name) return "";
  const withTitle = [title, name].filter(Boolean).join(" ").trim();
  if (/^(dr|mr|mrs|ms|miss|prof|professor)\.?\b/i.test(withTitle)) return withTitle;
  if (title) return withTitle;
  return `Dr ${name}`;
}

export function buildNewBillPrintData(options: {
  patient?: any;
  clinic?: {
    title?: string;
    clinicName?: string;
    address?: string;
    tpNo?: string;
    printImages?: string;
  };
  doctor?: {
    firstName?: string;
    lastName?: string;
    drName?: string;
    title?: string;
    designation?: string;
  };
  amounts?: any;
  treatments?: any[];
  subTreatments?: any[];
  /** Medicine sold on this receipt (display-only join; null when none). */
  medicine?: {
    total?: number;
    cash?: number;
    card?: number;
    bankTransfer?: number;
    lines?: {
      productName?: string;
      qty?: number;
      unitPrice?: number;
      total?: number;
    }[];
  } | null;
}): {
  previewData: NewBillPreviewData;
  previewTables: Record<string, ReportTable>;
} {
  const {
    patient,
    clinic,
    doctor,
    amounts,
    treatments = [],
    subTreatments = [],
    medicine = null,
  } = options;

  const age = patient?.dob ? calculateAge(patient.dob) : null;
  const invoiceNo = String(amounts?.invoiceNo ?? patient?.invoiceNo ?? "").trim();
  const total = Number(amounts?.total) || 0;
  const lastDue = Number(amounts?.lastDue) || 0;
  const discount = Number(amounts?.discount) || 0;
  const netotal = Number(amounts?.netotal) || 0;
  const card = Number(amounts?.card) || 0;
  const cash = Number(amounts?.cash) || 0;
  const bankTransfer = Number(amounts?.bankTransfer) || 0;
  // Due is treatment-only; the tender shown combines the medicine share so
  // the bill matches what the patient actually handed over.
  const balance = netotal - (card + cash + bankTransfer);
  const balanceLabel = balance > 0 ? "current due" : "current advance";
  const medicineTotal = Number(medicine?.total) || 0;
  const medicineLines = medicine?.lines ?? [];
  const hasMedicine = medicineTotal > 0 || medicineLines.length > 0;
  const displayCard = card + (Number(medicine?.card) || 0);
  const displayCash = cash + (Number(medicine?.cash) || 0);
  const displayBank = bankTransfer + (Number(medicine?.bankTransfer) || 0);
  const designation = String(doctor?.designation ?? "").trim();

  const clinicMerged = mergeClinicPreviewData(NEW_BILL_SAMPLE_DATA, {
    clinicName: clinic?.title || clinic?.clinicName,
    address: clinic?.address,
    tpNo: clinic?.tpNo,
    drName: doctorLabel(doctor),
    doctorDesignation: designation,
  });

  const previewData: NewBillPreviewData = {
    ...clinicMerged,
    doctorDesignation: designation || clinicMerged.doctorDesignation,
    invoiceNo: invoiceNo || clinicMerged.invoiceNo,
    invoiceDate:
      formatInvoiceDate(amounts?.paymentDate || amounts?.updatedAt || patient?.invoiceDate) ||
      clinicMerged.invoiceDate,
    // Patient identity never falls back to the designer sample data: a missing
    // field must print blank, not a plausible-looking demo value (age printed
    // the sample "22" on every bill while dob was absent from the API).
    regNo: String(patient?.regNo ?? "").trim(),
    patientName: String(patient?.fullName ?? "").trim(),
    patientAge: age != null ? String(age) : "",
    patientMobile: String(patient?.mobileNo ?? "").trim(),
    patientGender: String(patient?.gender ?? "").trim(),
    subTotal: money(total),
    lastDue: money(lastDue),
    discount: money(discount),
    netTotal: money(netotal),
    card: money(displayCard),
    cash: money(displayCash),
    bankTransfer: money(displayBank),
    balanceLabel,
    balanceAmount: money(Math.abs(balance)),
  };

  const printableTreatments = treatments.filter((item) => !isLongTermTreatment(item));
  const treatmentRows: string[][] = [];
  let index = 1;
  for (const item of printableTreatments) {
    const quantity = Number(item?.quantity) || 1;
    const price = Number(item?.price) || 0;
    treatmentRows.push([
      String(index++),
      String(item?.treatment ?? ""),
      money(quantity ? price / quantity : price),
      String(quantity),
      money(price),
    ]);
  }
  for (const item of subTreatments) {
    if (!shouldShowPrintSubTreatment(item, invoiceNo)) continue;
    const quantity = Number(item?.quantity) || 1;
    const price = parseFloat(item?.price) || 0;
    treatmentRows.push([
      String(index++),
      formatPrintSubTreatmentLabel(item),
      money(quantity ? price / quantity : price),
      String(quantity),
      money(price),
    ]);
  }

  if (hasMedicine) {
    treatmentRows.push(["", "Medicines", "", "", ""]);
    for (const line of medicineLines) {
      const qty = Number(line?.qty) || 0;
      treatmentRows.push([
        String(index++),
        String(line?.productName ?? ""),
        money(line?.unitPrice),
        String(qty),
        money(line?.total),
      ]);
    }
  }

  const amountRows: string[][] = [
    ["Sub Total", money(total)],
    ["Last due", money(lastDue)],
    ["Discount", money(discount)],
    ["Total", money(netotal)],
    ...(hasMedicine
      ? [
          ["Medicines", money(medicineTotal)],
          ["Grand Total", money(netotal + medicineTotal)],
        ]
      : []),
    ["Card", money(displayCard)],
    ["Cash", money(displayCash)],
    ["Bank Transfer", money(displayBank)],
    [balanceLabel, money(Math.abs(balance))],
  ];

  const previewTables: Record<string, ReportTable> = {
    treatmentTable: {
      columns: ["No", "Treatment", "Rate", "Units", "Price"],
      rows: treatmentRows.length ? treatmentRows : [["—", "No treatments", "", "", ""]],
    },
    amountTable: {
      columns: ["Description", "Amount"],
      hideHeader: true,
      rows: amountRows,
    },
  };

  return { previewData, previewTables };
}
